import { useState } from "react";
import { ASSIGNABLE_ROLES, isRegional, scopeOptionsFor, type AssignableRole, type PeopleScope } from "../../lib/roles";
import { assignRoleBulk, type OrgUnitOption } from "../../lib/admin";
import { messageOf } from "../../lib/errors";

/**
 * The selection bar: invite, suspend or restore access, or grant one role.
 *
 * ── BULK SUSPEND, AND WHY IT IS NOW HERE (139) ──────────────────────────────
 *
 * This bar used to refuse it: "Suspending forty people by mis-clicking a header
 * checkbox is an outage, and the person who could undo it may be among the
 * forty." David asked for it on 9 Oct, so the worry is answered instead:
 * set_access skips yourself and everyone who administers people (they stay
 * one at a time, in the panel), a typed reason is required before the button
 * will press, the count is in the button, and the outcome line offers Undo on
 * exactly the people this press suspended.
 *
 * ── GRANTS STILL GO THROUGH assign_role ─────────────────────────────────────
 *
 * assign_role_bulk loops rather than inserting a set, so every guard applies to
 * every person — membership, scopability, tenant scope, the org unit checks —
 * and every grant gets its own audit row. It is one transaction: if the
 * fortieth person fails, the first thirty-nine roll back with them, because a
 * half-applied bulk grant is the one nobody notices. Suspension is the
 * opposite on purpose: 39 of 40 leavers suspended beats 40 left signed in.
 */
export function BulkGrant({
  personIds,
  orgUnits,
  roles = ASSIGNABLE_ROLES,
  onInvite,
  onSuspend,
  onRestore,
  suspendedCount = 0,
  scope = null,
  onDone,
  onCancel,
}: {
  personIds: string[];
  orgUnits: OrgUnitOption[];
  /**
   * The roles the caller may hand out (137). An HR administrator does not see
   * Tenant or HR Administrator here, rather than seeing them and being refused.
   */
  roles?: AssignableRole[];
  /**
   * 138. Send the invitation email to the selection. The bar asks once ("Send
   * to 24?") because it is mail to real people; the server decides who is
   * actually invited and says why for the rest. Absent, no invite control.
   */
  onInvite?: () => Promise<void>;
  /** 139. Suspend the selection, with the reason typed here. Absent, no control. */
  onSuspend?: (reason: string) => Promise<void>;
  /** 139. Restore the suspended people in the selection. Shown when there are some. */
  onRestore?: () => Promise<void>;
  /** How many of the selection are suspended now. */
  suspendedCount?: number;
  /**
   * 153. A regional HR Administrator: a scopable role is granted for a unit in
   * the region only, so "Whole company" is not offered and a unit is required.
   */
  scope?: PeopleScope | null;
  onDone: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const [roleKey, setRoleKey] = useState("");
  const [orgUnitId, setOrgUnitId] = useState("");
  const [busy, setBusy] = useState(false);
  // Which act is asking "are you sure" — one at a time, so the bar never shows two questions.
  const [confirm, setConfirm] = useState<"invite" | "suspend" | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const role = roles.find((r) => r.key === roleKey);
  const regional = isRegional(scope);
  const scopeOptions = role ? scopeOptionsFor(role, orgUnits, scope) : [];
  const needsUnit = !!role?.scopable && regional;

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setConfirm(null);
      setReason("");
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  const n = personIds.length;

  async function grant() {
    setBusy(true);
    setError(null);
    try {
      await assignRoleBulk(
        personIds,
        roleKey,
        role?.scopable ? orgUnitId || null : null,
      );
      await onDone();
    } catch (e) {
      // Names the person who caused the failure, and says the rest rolled back.
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="bulkbar"
      role="region"
      aria-label="Act on the selected people"
    >
      <p className="bulkbar__count">
        <strong>{personIds.length}</strong> selected
      </p>

      {confirm === null &&
        (onInvite || onSuspend || (onRestore && suspendedCount > 0)) && (
          <div className="bulkbar__group">
            {onInvite && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={busy}
                onClick={() => setConfirm("invite")}
              >
                Send invitations
              </button>
            )}
            {onSuspend && (
              <button
                type="button"
                className="btn btn--danger btn--sm"
                disabled={busy}
                onClick={() => setConfirm("suspend")}
              >
                Suspend access
              </button>
            )}
            {onRestore && suspendedCount > 0 && (
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() => act(onRestore)}
              >
                {`Restore access (${suspendedCount})`}
              </button>
            )}
          </div>
        )}

      {confirm === "invite" && onInvite && (
        <div className="bulkbar__group">
          <span className="bulkbar__ask">Email an invitation to {n}?</span>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy}
            onClick={() => act(onInvite)}
          >
            {busy ? "Sending…" : "Send"}
          </button>
          <button
            type="button"
            className="btn btn--quiet btn--sm"
            disabled={busy}
            onClick={() => setConfirm(null)}
          >
            Cancel
          </button>
        </div>
      )}

      {confirm === "suspend" && onSuspend && (
        <div className="bulkbar__group bulkbar__group--danger">
          <span className="bulkbar__ask">Suspend access for {n}?</span>
          <label className="bulkbar__field bulkbar__reason">
            <span className="visually-hidden">
              Why (recorded in the audit log)
            </span>
            <input
              type="text"
              value={reason}
              autoFocus
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why — recorded on each person's audit row"
            />
          </label>
          <button
            type="button"
            className="btn btn--danger btn--sm"
            disabled={busy || !reason.trim()}
            onClick={() => act(() => onSuspend(reason))}
          >
            {busy ? "Suspending…" : `Suspend ${n}`}
          </button>
          <button
            type="button"
            className="btn btn--quiet btn--sm"
            disabled={busy}
            onClick={() => {
              setConfirm(null);
              setReason("");
            }}
          >
            Cancel
          </button>
          <p className="bulkbar__caution">
            They keep their roles and can be restored. You, and anyone who
            administers people, are skipped.
          </p>
        </div>
      )}

      {confirm === null && suspendedCount === 0 && (
        <>
          <span className="bulkbar__or">or grant</span>

          <label className="bulkbar__field">
            <span className="visually-hidden">Role</span>
            <select
              value={roleKey}
              onChange={(e) => setRoleKey(e.target.value)}
              disabled={busy}
            >
              <option value="">Choose a role…</option>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>

          {role?.scopable && (
            <label className="bulkbar__field">
              <span className="visually-hidden">Part of the company</span>
              <select
                value={orgUnitId}
                onChange={(e) => setOrgUnitId(e.target.value)}
                disabled={busy}
              >
                <option value="">{regional ? "Choose a part of your region…" : "Whole company"}</option>
                {scopeOptions.map((o) => (
                  <option key={o.orgUnitId} value={o.orgUnitId}>
                    {o.path}
                  </option>
                ))}
              </select>
            </label>
          )}

          <button
            type="button"
            className="btn btn--sm"
            disabled={busy || !roleKey || (needsUnit && !orgUnitId)}
            onClick={grant}
          >
            {busy ? "Granting…" : `Grant to ${personIds.length}`}
          </button>
        </>
      )}

      {confirm === null && (
        <button
          type="button"
          className="btn btn--quiet"
          disabled={busy}
          onClick={onCancel}
        >
          Clear
        </button>
      )}

      {/* The caution belongs here rather than after the click: this is the
          moment somebody is about to give a power to forty people at once. */}
      {confirm === null && role?.caution && (
        <p className="bulkbar__caution">{role.caution}</p>
      )}
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
